import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Chip,
  Avatar,
  Box,
  Typography,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Person as PersonIcon,
  Send as SendIcon,
  People as GroupIcon
} from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import {
  findDirectConversation,
  createConversation,
  sendMessage
} from '../../services/messagingService';
import logger from '../../utils/logger';

/**
 * New Message dialog — supports Direct Messages and Group Chat creation.
 *
 * Direct Message: pick one person, find-or-create 1:1 conversation.
 * Group Chat:     name the group, pick members, creates a persistent group conversation
 *                 that stays in the sidebar for everyone to message again.
 */
const NewMessageDialog = ({ open, onClose, onConversationCreated, familyCode }) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();

  const [messageType, setMessageType] = useState('direct');
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [firstMessage, setFirstMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [error, setError] = useState('');

  // Load family members
  useEffect(() => {
    if (!open || !familyCode) return;

    const loadMembers = async () => {
      setLoadingMembers(true);
      try {
        const membersSnapshot = await getDocs(collection(db, 'families', familyCode, 'members'));

        const members = [];
        membersSnapshot.forEach((doc) => {
          const data = doc.data();
          if (doc.id !== currentUser?.uid) {
            members.push({
              id: doc.id,
              name: data.displayName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email,
              photoURL: data.photoURL || null,
              email: data.email
            });
          }
        });
        setFamilyMembers(members);
      } catch (err) {
        logger.error('Error loading family members:', err);
        setError('Failed to load family members');
      } finally {
        setLoadingMembers(false);
      }
    };

    loadMembers();
  }, [open, familyCode, currentUser?.uid]);

  const handleSend = async () => {
    if (selectedRecipients.length === 0) {
      setError('Please select at least one recipient');
      return;
    }
    if (messageType === 'group' && selectedRecipients.length < 1) {
      setError('Group chat requires at least 1 other member');
      return;
    }
    // Direct messages require a first message; group chats can be created without one
    if (messageType === 'direct' && !firstMessage.trim()) {
      setError('Please type a message');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const authorName = profile?.displayName
        || `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim()
        || currentUser.displayName
        || 'User';

      let conversationId;
      const allParticipants = [currentUser.uid, ...selectedRecipients.map(r => r.id)];

      if (messageType === 'direct') {
        // Direct message — find or create
        const existingId = await findDirectConversation(
          familyCode,
          currentUser.uid,
          selectedRecipients[0].id
        );

        if (existingId) {
          conversationId = existingId;
        } else {
          conversationId = await createConversation(familyCode, {
            type: 'direct',
            participants: allParticipants,
            name: null,
            createdBy: currentUser.uid,
            createdByName: authorName
          });
        }
      } else {
        // Group chat — create new group
        const name = groupName.trim() || selectedRecipients.map(r => r.name).join(', ');
        conversationId = await createConversation(familyCode, {
          type: 'group',
          participants: allParticipants,
          name,
          createdBy: currentUser.uid,
          createdByName: authorName
        });
      }

      // Send the first message (if provided)
      if (firstMessage.trim()) {
        await sendMessage(familyCode, conversationId, {
          content: firstMessage.trim(),
          authorId: currentUser.uid,
          authorName,
          authorPhoto: currentUser.photoURL || profile?.photoURL || null
        });
      }

      // Reset form and notify parent
      handleClose();
      onConversationCreated?.(conversationId);
    } catch (err) {
      logger.error('Error creating conversation:', err);
      setError(err.message || 'Failed to create conversation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedRecipients([]);
    setGroupName('');
    setFirstMessage('');
    setError('');
    setMessageType('direct');
    onClose();
  };

  const handleTypeChange = (_, newType) => {
    if (newType) {
      setMessageType(newType);
      setSelectedRecipients([]);
      setGroupName('');
      setError('');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Message</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Message type toggle */}
        <ToggleButtonGroup
          value={messageType}
          exclusive
          onChange={handleTypeChange}
          fullWidth
          size="small"
          sx={{ mb: 2, mt: 1 }}
        >
          <ToggleButton value="direct">
            <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
            Direct Message
          </ToggleButton>
          <ToggleButton value="group">
            <GroupIcon sx={{ mr: 1, fontSize: 20 }} />
            Group Chat
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Group name — shown first in group mode */}
        {messageType === 'group' && (
          <TextField
            fullWidth
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="e.g. Siblings, Game Night Crew, Trip Planning..."
            sx={{ mb: 2 }}
            size="small"
            helperText="Give your group a name so everyone can find it easily"
          />
        )}

        {/* Show current user as a permanent group member */}
        {messageType === 'group' && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Members
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
              <Chip
                avatar={
                  <Avatar src={currentUser?.photoURL || profile?.photoURL} sx={{ width: 24, height: 24 }}>
                    <PersonIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                }
                label={`${profile?.displayName || profile?.firstName || currentUser?.displayName || 'You'} (You)`}
                size="small"
                color="primary"
                variant="outlined"
              />
              {selectedRecipients.map((r) => (
                <Chip
                  key={r.id}
                  avatar={
                    <Avatar src={r.photoURL} sx={{ width: 24, height: 24 }}>
                      <PersonIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                  }
                  label={r.name}
                  size="small"
                  onDelete={() => setSelectedRecipients(prev => prev.filter(p => p.id !== r.id))}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Recipient selection */}
        <Autocomplete
          multiple
          options={familyMembers}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedRecipients}
          onChange={(_, newValue) => {
            if (messageType === 'direct') {
              // In direct mode, only allow 1 recipient
              setSelectedRecipients(newValue.length > 1 ? [newValue[newValue.length - 1]] : newValue);
            } else {
              setSelectedRecipients(newValue);
            }
          }}
          loading={loadingMembers}
          renderTags={(value, getTagProps) =>
            messageType === 'group'
              ? null  // Group mode shows chips separately above with the "You" chip
              : value.map((option, index) => (
                  <Chip
                    key={option.id}
                    avatar={
                      <Avatar src={option.photoURL} sx={{ width: 24, height: 24 }}>
                        <PersonIcon sx={{ fontSize: 14 }} />
                      </Avatar>
                    }
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
          }
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', gap: 1.5, py: 1 }}>
                <Avatar src={option.photoURL} sx={{ width: 32, height: 32 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                </Box>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={messageType === 'direct' ? 'To' : 'Add Members'}
              placeholder={messageType === 'direct' ? 'Search family members...' : 'Search and add members...'}
              sx={{ mb: 2 }}
            />
          )}
          noOptionsText="No members found"
          disableCloseOnSelect={messageType === 'group'}
        />

        {/* Group total count */}
        {messageType === 'group' && selectedRecipients.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {selectedRecipients.length + 1} total members. This group will be saved in everyone's Messages.
          </Typography>
        )}

        {/* First message */}
        <TextField
          fullWidth
          label={messageType === 'group' ? 'First Message (optional)' : 'Message'}
          value={firstMessage}
          onChange={(e) => setFirstMessage(e.target.value)}
          placeholder={messageType === 'group' ? 'Say something to the group, or create it first and message later...' : 'Type your message...'}
          multiline
          rows={3}
          disabled={loading}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={
            loading ||
            selectedRecipients.length === 0 ||
            (messageType === 'direct' && !firstMessage.trim()) ||
            (messageType === 'group' && selectedRecipients.length < 1)
          }
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
        >
          {loading
            ? (messageType === 'group' ? 'Creating...' : 'Sending...')
            : messageType === 'group'
              ? (firstMessage.trim() ? 'Create Group & Send' : 'Create Group')
              : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewMessageDialog;
